import { Maybe } from '@/types/globals';
import { Injectable } from '@angular/core';
import { LoggerService } from '@core/services/logger/logger.service';
import { ImplantsViewDataChanged } from '@itero/itero-client-communication';
import { ImplantsViewDataRxApi } from '@modules/teeth-diagram/models/implants-view-data';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { ApplicationModes, SoftwareOptions } from '@shared/models/enums/enums';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { combineLatest, filter, map, Observable, shareReplay } from 'rxjs';
import { ImplantsViewAppParameter } from './models/implants-view-app-parameter';

@Injectable({ providedIn: 'root' })
export class ImplantsViewService {
	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly logger: LoggerService,
		private readonly hostPlatformService: HostPlatformService
	) {}

	shouldShowNewImplantsView$: Observable<boolean> = combineLatest([this.shellQuery.rxConfiguration$, this.shellQuery.teeth$]).pipe(
		filter(([rxConfiguration]) => !!rxConfiguration),
		map(([rxConfiguration, teeth]) =>
			this.isNewImplantsViewEligible(rxConfiguration?.CompanyConfiguration?.SoftwareOptionsForCompany ?? [], teeth ?? [])
		),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	showOfflineBanner$(isReadOnly$: Observable<boolean>): Observable<boolean> {
		return combineLatest([this.hostPlatformService.isOfflineModeOnScanner$, isReadOnly$]).pipe(
			map(([isOfflineOnScanner, isReadOnly]) => !isReadOnly && isOfflineOnScanner)
		);
	}

	shouldShowNewImplantsView(): boolean {
		const rxConfiguration = this.shellQuery.rxConfiguration;
		const companySoftwareOptions = rxConfiguration?.CompanyConfiguration?.SoftwareOptionsForCompany ?? [];
		const teeth = this.shellQuery.getValue().rx?.Teeth;

		return this.isNewImplantsViewEligible(companySoftwareOptions, teeth ?? []);
	}

	/**
	 * RxLaunchViaSLOnScanner applies on scanner only; MIDC/web uses ITeroImplantsSystem alone.
	 * Returns false when required SWOs are missing or any tooth has legacy ImplantTypeID data.
	 */
	isNewImplantsViewEligible(companySoftwareOptions: number[], teeth: Tooth[]): boolean {
		const appMode = this.shellQuery.applicationMode as ApplicationModes;
		const requiredSoftwareOptions = HostPlatformService.isScanner(appMode)
			? [SoftwareOptions.ITeroImplantsSystem, SoftwareOptions.RxLaunchViaSLOnScanner]
			: [SoftwareOptions.ITeroImplantsSystem];

		if (!requiredSoftwareOptions.every(option => companySoftwareOptions.includes(option))) {
			return false;
		}

		if (teeth?.some((tooth: Tooth) => !!tooth?.ImplantTypeID)) {
			return false;
		}

		return true;
	}

	prepareImplantsViewPreselectedData(
		tooth: Tooth
	): Pick<
		ImplantsViewAppParameter,
		| 'toothNumber'
		| 'preselectedImplantManufacturerId'
		| 'preselectedImplantConnectionId'
		| 'preselectedImplantPlatformId'
		| 'preselectedScanBodyId'
	> {
		return {
			toothNumber: tooth.ToothID ?? 0,
			preselectedImplantManufacturerId: tooth?.ImplantsViewData?.ImplantManufacturerId,
			preselectedImplantConnectionId: tooth?.ImplantsViewData?.ImplantConnectionId,
			preselectedImplantPlatformId: tooth?.ImplantsViewData?.ImplantPlatformId,
			preselectedScanBodyId: tooth?.ImplantsViewData?.ScanBodyId
		};
	}

	parseImplantsViewEventToothId(toothNumber: number | string | null | undefined): number | null {
		if (toothNumber === undefined || toothNumber === null) {
			return null;
		}

		const n = typeof toothNumber === 'number' ? toothNumber : Number.parseInt(String(toothNumber), 10);

		return Number.isFinite(n) ? n : null;
	}

	parseImplantsViewDataChangedEvent(
		event: Maybe<ImplantsViewDataChanged>
	): { toothId: number; implantsViewData: ImplantsViewDataRxApi } | null {
		const data = event?.data;

		if (!data) {
			this.logger.warn('ImplantsViewDataChanged event has no data', { module: 'ImplantsViewService' });

			return null;
		}

		if (!data.toothNumber) {
			this.logger.warn('ImplantsViewDataChanged event data is missing toothNumber', { module: 'ImplantsViewService' });

			return null;
		}

		const toothId = this.parseImplantsViewEventToothId(data.toothNumber);

		if (toothId == null) {
			this.logger.warn('ImplantsViewDataChanged event data has invalid toothNumber', { module: 'ImplantsViewService' });

			return null;
		}

		// eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-unused-vars
		const { toothNumber: _tn, ...implantsPayload } = data;

		return {
			toothId,
			implantsViewData: {
				ImplantManufacturerId: implantsPayload.implantManufacturer?.id,
				ImplantManufacturerName: implantsPayload.implantManufacturer?.name,
				ImplantConnectionId: implantsPayload.implantConnection?.id,
				ImplantConnectionName: implantsPayload.implantConnection?.name,
				ImplantPlatformId: implantsPayload.implantPlatform?.id,
				ImplantPlatformName: implantsPayload.implantPlatform?.name,
				ScanBodyId: implantsPayload.scanBody?.id,
				ScanBodyName: implantsPayload.scanBody?.name,
				PartNumber: implantsPayload.scanBody?.partNumber,
				PartManufacturerName: implantsPayload.scanBody?.partManufacturerName,
				LibrarySuppliers: implantsPayload.scanBody?.librarySuppliers
			}
		};
	}
}
